// ============================================================================
// Cancellation metrics — rate trend, lead time, reasons, resold nights.
// A cancelled stay is a current-version reservation with status 'cancelled';
// its cancelled_at / cancellation_reason live on reservation_sites rows.
// ============================================================================

import { addDays, daysBetween, getLocalDateString } from '../dateUtils';
import { bucketKey, bucketLabel, enumerateBuckets, groupBy } from './aggregations';
import { expandSiteNights } from './occupancyMetrics';
import { AnalysisStay, Granularity, isRevenueStay } from './types';

export function isCancelledStay(stay: AnalysisStay): boolean {
  return stay.isCurrentVersion && stay.status === 'cancelled';
}

export function cancelledAt(stay: AnalysisStay): string | undefined {
  return stay.sites.find(site => site.cancelledAt)?.cancelledAt;
}

// ============================================================================
// Cancellation rate trend
// ============================================================================

export interface CancellationPoint {
  bucket: string;
  label: string;
  cancelled: number;
  booked: number;
  rate: number; // percent of bookings for the bucket that were cancelled
}

/** Buckets by check-in date: of stays due to arrive in the bucket, how many were cancelled. */
export function cancellationRateSeries(
  stays: AnalysisStay[],
  from: string,
  to: string,
  granularity: Granularity
): CancellationPoint[] {
  const buckets = enumerateBuckets(from, to, granularity);
  const counts = new Map<string, { cancelled: number; booked: number }>(
    buckets.map(b => [b, { cancelled: 0, booked: 0 }])
  );
  for (const stay of stays) {
    if (!stay.isCurrentVersion) continue;
    if (stay.checkIn < from || stay.checkIn > to) continue;
    const bucket = counts.get(bucketKey(stay.checkIn, granularity));
    if (!bucket) continue;
    if (stay.status === 'cancelled') {
      bucket.cancelled += 1;
      bucket.booked += 1;
    } else if (isRevenueStay(stay)) {
      bucket.booked += 1;
    }
  }
  return buckets.map(key => {
    const { cancelled, booked } = counts.get(key)!;
    return {
      bucket: key,
      label: bucketLabel(key, granularity),
      cancelled,
      booked,
      rate: booked > 0 ? Math.round((cancelled / booked) * 1000) / 10 : 0,
    };
  });
}

export function overallCancellationRate(stays: AnalysisStay[], from: string, to: string): number | null {
  const series = cancellationRateSeries(stays, from, to, 'month');
  const cancelled = series.reduce((acc, p) => acc + p.cancelled, 0);
  const booked = series.reduce((acc, p) => acc + p.booked, 0);
  if (booked === 0) return null;
  return Math.round((cancelled / booked) * 1000) / 10;
}

// ============================================================================
// Cancellation lead time (cancelled_at → would-be check-in)
// ============================================================================

export interface CancellationLeadBucket {
  label: string;
  count: number;
}

const LEAD_BUCKETS: Array<{ label: string; min: number; max: number }> = [
  { label: 'Day-of / after', min: -Infinity, max: 0 },
  { label: '1–7 days out', min: 1, max: 7 },
  { label: '8–30 days out', min: 8, max: 30 },
  { label: '30+ days out', min: 31, max: Infinity },
];

export function cancellationLeadBuckets(
  stays: AnalysisStay[],
  from: string,
  to: string
): CancellationLeadBucket[] {
  const buckets = LEAD_BUCKETS.map(b => ({ label: b.label, count: 0 }));
  for (const stay of stays) {
    if (!isCancelledStay(stay)) continue;
    if (stay.checkIn < from || stay.checkIn > to) continue;
    const when = cancelledAt(stay);
    if (!when) continue;
    const lead = daysBetween(getLocalDateString(new Date(when)), stay.checkIn);
    const idx = LEAD_BUCKETS.findIndex(b => lead >= b.min && lead <= b.max);
    if (idx >= 0) buckets[idx].count += 1;
  }
  return buckets;
}

export function avgCancellationLeadDays(stays: AnalysisStay[], from: string, to: string): number | null {
  const leads: number[] = [];
  for (const stay of stays) {
    if (!isCancelledStay(stay)) continue;
    if (stay.checkIn < from || stay.checkIn > to) continue;
    const when = cancelledAt(stay);
    if (!when) continue;
    leads.push(daysBetween(getLocalDateString(new Date(when)), stay.checkIn));
  }
  if (leads.length === 0) return null;
  return Math.round((leads.reduce((a, b) => a + b, 0) / leads.length) * 10) / 10;
}

// ============================================================================
// Cancellation reasons
// ============================================================================

export interface ReasonCount {
  reason: string;
  count: number;
}

export function cancellationReasons(stays: AnalysisStay[], from: string, to: string): ReasonCount[] {
  const cancelled = stays
    .filter(isCancelledStay)
    .filter(s => s.checkIn >= from && s.checkIn <= to);
  const grouped = groupBy(cancelled, stay => {
    const reason = stay.sites.find(site => site.cancellationReason)?.cancellationReason;
    return (reason || 'Not recorded').trim().toLowerCase();
  });
  return Array.from(grouped.entries())
    .map(([reason, items]) => ({
      reason: reason.charAt(0).toUpperCase() + reason.slice(1),
      count: items.length,
    }))
    .sort((a, b) => b.count - a.count);
}

// ============================================================================
// Resold cancelled nights
// ============================================================================

export interface ResoldStats {
  cancelledSiteNights: number;
  resoldSiteNights: number;
  resoldPct: number | null;
}

/** Of the site-nights freed by cancellations, how many were re-occupied. */
export function resoldCancelledNights(stays: AnalysisStay[], from: string, to: string): ResoldStats {
  const { byDate } = expandSiteNights(stays, from, to); // revenue stays only
  let cancelledSiteNights = 0;
  let resoldSiteNights = 0;
  for (const stay of stays) {
    if (!isCancelledStay(stay)) continue;
    for (const site of stay.sites) {
      let night = stay.checkIn > from ? stay.checkIn : from;
      while (night < stay.checkOut && night <= to) {
        cancelledSiteNights += 1;
        if (byDate.get(night)?.has(site.siteId)) resoldSiteNights += 1;
        night = addDays(night, 1);
      }
    }
  }
  return {
    cancelledSiteNights,
    resoldSiteNights,
    resoldPct:
      cancelledSiteNights > 0
        ? Math.round((resoldSiteNights / cancelledSiteNights) * 1000) / 10
        : null,
  };
}
